import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaWeight, FaSmile, FaBullseye, FaCheckCircle, FaUtensils, FaDumbbell } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ExcludedExercisesSelector from '@/components/ExcludedExercisesSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEELING_OPTIONS = [
  { value: 'excelente', label: 'Excelente', emoji: '🔥', description: 'Me siento increíble, lleno de energía' },
  { value: 'bien', label: 'Bien', emoji: '💪', description: 'Todo va bien, estoy progresando' },
  { value: 'masomenos', label: 'Más o menos', emoji: '😐', description: 'Algunos días buenos, otros no tanto' },
  { value: 'dificil', label: 'Ha sido difícil', emoji: '😓', description: 'Me ha costado mantener el ritmo' }
];

const WORKOUT_GOALS = [
  { value: 'perder_grasa', label: 'Perder Grasa', icon: '🔥' },
  { value: 'ganar_musculo', label: 'Ganar Músculo', icon: '💪' },
  { value: 'tonificar', label: 'Tonificar', icon: '✨' },
  { value: 'mantener', label: 'Mantener', icon: '⚖️' },
  { value: 'aumentar_fuerza', label: 'Aumentar Fuerza', icon: '🏋️' },
  { value: 'mejorar_resistencia', label: 'Mejorar Resistencia', icon: '🏃' }
];

const DIET_GOALS = [
  { value: 'deficit', label: 'Déficit Calórico', icon: '📉', description: 'Perder peso' },
  { value: 'superavit', label: 'Superávit Calórico', icon: '📈', description: 'Ganar masa' },
  { value: 'mantenimiento', label: 'Mantenimiento', icon: '⚖️', description: 'Mantener peso' },
  { value: 'limpiar_alimentacion', label: 'Limpiar Alimentación', icon: '🥗', description: 'Comer más saludable' }
];

const DIET_FEELING_OPTIONS = [
  { value: 'facil', label: 'Fácil de seguir', emoji: '😊', description: 'Me adapté bien a la dieta' },
  { value: 'moderado', label: 'Moderado', emoji: '🙂', description: 'Algunos ajustes fueron necesarios' },
  { value: 'dificil', label: 'Difícil', emoji: '😅', description: 'Me costó seguirla al 100%' },
  { value: 'muy_dificil', label: 'Muy difícil', emoji: '😰', description: 'Necesito cambios importantes' }
];

export default function ContinuationForm({ formType = 'workout', onSuccess, onCancel, isFirstMonth = false }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentWeight: '',
    currentHeight: '', // Estatura nueva
    feeling: '',
    newGoal: '',
    // Para dieta
    dietFeeling: '',
    newAllergies: '',
    mealsPerDay: '',
    calorieAdjustment: 'mantener', // 'aumentar' (+10%), 'disminuir' (-10%), 'mantener'
    // Para rutina
    changedMuscles: [],
    problematicExercises: '',
    excludedExerciseIds: [],
    trainingDaysChange: '',
    // === Fase 3 · Señal de progreso · auto-ajusta cardio ===
    resultFeeling: '',       // 'positivo' | 'sin_cambios' | 'negativo'
    weightChangeKg: '',      // diferencia respecto al mes anterior (positivo=subió)
    wantsMoreIntensity: false,
  });

  const isWorkout = formType === 'workout';
  const totalSteps = 3;

  // 2026-02 · Template dinámico `asesoria_update` — labels editables desde admin.
  const [formTemplate, setFormTemplate] = useState(null);
  const tplField = (id) => (formTemplate?.fields || []).find(f => f.id === id) || null;
  const tplLabel = (id, fallback) => tplField(id)?.label || fallback;
  const tplDesc = (id, fallback = '') => tplField(id)?.description || fallback;
  const tplOptions = (id, fallback) => {
    const f = tplField(id);
    return Array.isArray(f?.options) && f.options.length > 0 ? f.options : fallback;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // Intentamos con los planes de asesoría en cascada — el endpoint
        // devuelve el primero que tenga template activo.
        for (const plan of ['premium', 'gold', 'competitive']) {
          const res = await axios.get(
            `${API}/form-templates/active?form_type=asesoria_update&plan=${plan}`,
            { headers, withCredentials: false }
          );
          if (res.data?.template) {
            if (!cancelled) setFormTemplate(res.data.template);
            return;
          }
        }
      } catch { /* fallback silencioso */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const endpoint = isWorkout ? '/workout/continuation' : '/diet/continuation';
      const response = await axios.post(`${API}${endpoint}`, {
        ...formData,
        // Coerción explícita para el backend
        weightChangeKg: formData.weightChangeKg === '' || formData.weightChangeKg === null ? null : parseFloat(formData.weightChangeKg),
        form_type: formType
      }, { 
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        toast.success(isWorkout 
          ? '¡Formulario de continuación enviado! Tu nueva rutina será preparada pronto.'
          : '¡Formulario de continuación enviado! Tu nueva dieta será preparada pronto.'
        );
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error submitting continuation form:', error);
      toast.error('Error al enviar el formulario. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const renderWorkoutStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaWeight className="inline mr-2 text-[#00F0FF]" />
          {tplLabel('currentWeight', '¿Cuál es tu peso actual? (kg)')}
        </label>
        <input
          type="number"
          value={formData.currentWeight}
          onChange={(e) => setFormData({ ...formData, currentWeight: e.target.value })}
          placeholder={tplField('currentWeight')?.placeholder || 'Ej: 65'}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)] transition-all"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          {tplLabel('currentHeight', '📏 ¿Cuál es tu estatura actual? (cm)')}
        </label>
        <input
          type="number"
          value={formData.currentHeight}
          onChange={(e) => setFormData({ ...formData, currentHeight: e.target.value })}
          placeholder={tplField('currentHeight')?.placeholder || 'Ej: 165'}
          aria-invalid={formData.currentHeight !== '' && !isNaN(parseFloat(formData.currentHeight)) && parseFloat(formData.currentHeight) < 100}
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white placeholder-gray-500 focus:ring-1 transition-all ${formData.currentHeight !== '' && !isNaN(parseFloat(formData.currentHeight)) && parseFloat(formData.currentHeight) < 100 ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]'}`}
        />
        {formData.currentHeight !== '' && !isNaN(parseFloat(formData.currentHeight)) && parseFloat(formData.currentHeight) < 100 && (
          <p className="text-sm font-semibold text-red-500 mt-1" data-testid="height-error">
            Ingresa tu estatura en centímetros (ejemplo: 179)
          </p>
        )}
      </div>
      
      {/* Ajuste de calorías - solo después del primer mes */}
      {!isFirstMonth && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            {tplLabel('calorieAdjustment', '🔥 ¿Quieres ajustar tus calorías para el próximo mes?')}
          </label>
          <div className="grid grid-cols-3 gap-3">
            {(tplOptions('calorieAdjustment', [
              { value: 'aumentar', label: '+10% Calorías', icon: '📈', description: 'Comer un poco más' },
              { value: 'mantener', label: 'Mantener', icon: '⚖️', description: 'Sin cambios' },
              { value: 'disminuir', label: '-10% Calorías', icon: '📉', description: 'Comer un poco menos' }
            ])).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData({ ...formData, calorieAdjustment: option.value })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.calorieAdjustment === option.value
                    ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10'
                    : 'border-white/10 bg-white/5 hover:border-white/30'
                }`}
              >
                <span className="text-2xl block mb-1">{option.icon || ''}</span>
                <span className="text-sm font-medium text-white block">{option.label}</span>
                <span className="text-xs text-gray-400">{option.description || option.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaSmile className="inline mr-2 text-[var(--brand-primary)]" />
          {tplLabel('feeling', '¿Cómo te sentiste con tu rutina este mes?')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          {FEELING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, feeling: option.value })}
              className={`p-4 rounded-xl border text-left transition-all ${
                formData.feeling === option.value
                  ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/20'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <span className="text-2xl mb-2 block">{option.emoji}</span>
              <span className="font-medium text-white block">{option.label}</span>
              <span className="text-xs text-gray-400">{option.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* === Fase 3 · Progreso Mensual — auto-ajusta el cardio === */}
      <div className="p-4 rounded-xl border border-emerald-500/25 bg-emerald-500/5 space-y-4" data-testid="progress-signal-block">
        <div className="flex items-center gap-2">
          <span className="text-emerald-400 text-lg">📊</span>
          <div>
            <p className="font-bold text-white">¿Cómo van tus resultados?</p>
            <p className="text-xs text-gray-400">Con esto ajustamos tu cardio automáticamente el próximo mes</p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2">Sensación general con tus resultados</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: 'positivo', l: '😊 Voy bien', c: '#34D399' },
              { v: 'sin_cambios', l: '😐 Sin cambios', c: '#FBBF24' },
              { v: 'negativo', l: '😞 No me siento bien', c: '#F87171' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`result-feeling-${o.v}`}
                onClick={() => setFormData({ ...formData, resultFeeling: o.v })}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                  formData.resultFeeling === o.v
                    ? 'text-white border-transparent'
                    : 'text-gray-300 border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                style={formData.resultFeeling === o.v ? { background: o.c } : {}}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2">Cambio de peso vs. mes anterior (opcional)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.weightChangeKg}
              onChange={(e) => setFormData({ ...formData, weightChangeKg: e.target.value })}
              placeholder="Ej: -1.5 (bajaste 1.5 kg) o 0.8 (subiste)"
              className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm"
              data-testid="weight-change-input"
            />
            <span className="text-xs text-gray-500">kg</span>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.wantsMoreIntensity}
            onChange={(e) => setFormData({ ...formData, wantsMoreIntensity: e.target.checked })}
            className="w-4 h-4 accent-emerald-500"
            data-testid="wants-more-intensity"
          />
          <span className="text-sm text-gray-300">Quiero más intensidad este mes</span>
        </label>
      </div>
    </div>
  );

  const renderWorkoutStep2 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaBullseye className="inline mr-2 text-[#CCFF00]" />
          ¿Cuál es tu objetivo para el próximo mes?
        </label>
        <div className="grid grid-cols-2 gap-3">
          {WORKOUT_GOALS.map((goal) => (
            <button
              key={goal.value}
              type="button"
              onClick={() => setFormData({ ...formData, newGoal: goal.value })}
              className={`p-4 rounded-xl border text-center transition-all ${
                formData.newGoal === goal.value
                  ? 'border-[#CCFF00] bg-[#CCFF00]/20'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <span className="text-2xl mb-2 block">{goal.icon}</span>
              <span className="font-medium text-white">{goal.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          ¿Algún ejercicio te generó molestias? (opcional)
        </label>
        <ExcludedExercisesSelector
          value={formData.excludedExerciseIds}
          onChange={(next) => setFormData({ ...formData, excludedExerciseIds: next })}
          testIdPrefix="continuation"
        />
        <p className="text-xs text-gray-400 italic mt-2">
          Si no marcas ninguno, damos por entendido que puedes realizar todos los ejercicios. Deja esta casilla libre si aún no has empezado a entrenar.
        </p>
      </div>
    </div>
  );

  const renderWorkoutStep3 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          {tplLabel('trainingDaysChange', '¿Quieres cambiar los días de entrenamiento? (opcional)')}
        </label>
        <select
          value={formData.trainingDaysChange}
          onChange={(e) => setFormData({ ...formData, trainingDaysChange: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)] transition-all"
        >
          <option value="">Mantener igual</option>
          <option value="3">3 días por semana</option>
          <option value="4">4 días por semana</option>
          <option value="5">5 días por semana</option>
          <option value="6">6 días por semana</option>
        </select>
      </div>

      <div className="rounded-xl p-4 bg-gradient-to-r from-[var(--brand-primary)]/20 to-[#00F0FF]/20 border border-[var(--brand-primary)]/30">
        <h4 className="font-semibold text-white mb-2">📋 Resumen de tu continuación</h4>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• Peso actual: <span className="text-white font-medium">{formData.currentWeight || '-'} kg</span></li>
          <li>• Te sentiste: <span className="text-white font-medium">{FEELING_OPTIONS.find(f => f.value === formData.feeling)?.label || '-'}</span></li>
          <li>• Nuevo objetivo: <span className="text-white font-medium">{WORKOUT_GOALS.find(g => g.value === formData.newGoal)?.label || '-'}</span></li>
        </ul>
      </div>
    </div>
  );

  const renderDietStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaWeight className="inline mr-2 text-[#00F0FF]" />
          {tplLabel('currentWeight', '¿Cuál es tu peso actual? (kg)')}
        </label>
        <input
          type="number"
          value={formData.currentWeight}
          onChange={(e) => setFormData({ ...formData, currentWeight: e.target.value })}
          placeholder={tplField('currentWeight')?.placeholder || 'Ej: 65'}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)] transition-all"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaUtensils className="inline mr-2 text-[var(--brand-primary)]" />
          {tplLabel('dietFeeling', '¿Cómo te fue siguiendo la dieta?')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          {DIET_FEELING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, dietFeeling: option.value })}
              className={`p-4 rounded-xl border text-left transition-all ${
                formData.dietFeeling === option.value
                  ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/20'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <span className="text-2xl mb-2 block">{option.emoji}</span>
              <span className="font-medium text-white block">{option.label}</span>
              <span className="text-xs text-gray-400">{option.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDietStep2 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          <FaBullseye className="inline mr-2 text-[#CCFF00]" />
          ¿Cuál es tu objetivo nutricional para el próximo mes?
        </label>
        <div className="grid grid-cols-2 gap-3">
          {DIET_GOALS.map((goal) => (
            <button
              key={goal.value}
              type="button"
              onClick={() => setFormData({ ...formData, newGoal: goal.value })}
              className={`p-4 rounded-xl border text-center transition-all ${
                formData.newGoal === goal.value
                  ? 'border-[#CCFF00] bg-[#CCFF00]/20'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <span className="text-2xl mb-2 block">{goal.icon}</span>
              <span className="font-medium text-white block">{goal.label}</span>
              <span className="text-xs text-gray-400">{goal.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          ¿Desarrollaste alguna nueva alergia o intolerancia? (opcional)
        </label>
        <textarea
          value={formData.newAllergies}
          onChange={(e) => setFormData({ ...formData, newAllergies: e.target.value })}
          placeholder="Ej: Me di cuenta que el gluten me cae mal..."
          rows={2}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)] transition-all"
        />
      </div>
    </div>
  );

  const renderDietStep3 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          {tplLabel('mealsPerDay', '¿Cuántas comidas prefieres al día? (opcional)')}
        </label>
        <select
          value={formData.mealsPerDay}
          onChange={(e) => setFormData({ ...formData, mealsPerDay: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)] transition-all"
        >
          <option value="">Mantener igual</option>
          <option value="3">3 comidas principales</option>
          <option value="4">4 comidas (3 + snack)</option>
          <option value="5">5 comidas (3 + 2 snacks)</option>
          <option value="6">6 comidas pequeñas</option>
        </select>
      </div>

      <div className="rounded-xl p-4 bg-gradient-to-r from-[var(--brand-primary)]/20 to-[#00F0FF]/20 border border-[var(--brand-primary)]/30">
        <h4 className="font-semibold text-white mb-2">📋 Resumen de tu continuación</h4>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• Peso actual: <span className="text-white font-medium">{formData.currentWeight || '-'} kg</span></li>
          <li>• Experiencia con la dieta: <span className="text-white font-medium">{DIET_FEELING_OPTIONS.find(f => f.value === formData.dietFeeling)?.label || '-'}</span></li>
          <li>• Nuevo objetivo: <span className="text-white font-medium">{DIET_GOALS.find(g => g.value === formData.newGoal)?.label || '-'}</span></li>
        </ul>
      </div>
    </div>
  );

  const renderStep = () => {
    if (isWorkout) {
      switch (step) {
        case 1: return renderWorkoutStep1();
        case 2: return renderWorkoutStep2();
        case 3: return renderWorkoutStep3();
        default: return null;
      }
    } else {
      switch (step) {
        case 1: return renderDietStep1();
        case 2: return renderDietStep2();
        case 3: return renderDietStep3();
        default: return null;
      }
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.currentWeight && (isWorkout ? formData.feeling : formData.dietFeeling);
    }
    if (step === 2) {
      return formData.newGoal;
    }
    return true;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <Card className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-[var(--brand-primary)]/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            {isWorkout ? <FaDumbbell className="text-[var(--brand-primary)]" /> : <FaUtensils className="text-[var(--brand-primary)]" />}
            Formulario de Continuación - {isWorkout ? 'Rutina' : 'Dieta'}
          </CardTitle>
          <p className="text-sm text-gray-400">
            ¡Es hora de actualizar tu plan! Cuéntanos cómo te fue este mes.
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-[var(--brand-primary)]' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">Paso {step} de {totalSteps}</p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {renderStep()}
          
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Anterior
              </Button>
            )}
            {onCancel && step === 1 && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
            )}
            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="flex-1 text-white"
                style={{ 
                  background: canProceed() 
                    ? 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' 
                    : 'rgba(255,255,255,0.1)'
                }}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="flex-1 text-white"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                {loading ? 'Enviando...' : (
                  <>
                    <FaCheckCircle className="mr-2" /> Enviar Formulario
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
